import { useCallback, useEffect, useRef } from 'react';
import { useMailEditorStore } from '@/stores/mailEditorStore';
import type { MailEditorRef } from '@/components/mail-editor';
import { editDraftEmailPost } from '@/services/swagger/easourcing/WriteService';
import type { AttachmentsModel } from '@/stores/mailEditorStore';
import { mergeHtmlContent } from './useMergeHtmlContent';

/**
 * 从草稿保存响应中提取 contentId
 */
export function extractContentIdFromResponse(draftResponse: unknown): string | undefined {
  if (!draftResponse || typeof draftResponse !== 'object') {
    return undefined;
  }

  // 如果响应直接包含 contentId
  if ('contentId' in draftResponse && draftResponse.contentId) {
    return String(draftResponse.contentId);
  }

  // 如果响应有 data 字段且 data 包含 contentId
  if (
    'data' in draftResponse &&
    draftResponse.data &&
    typeof draftResponse.data === 'object' &&
    'contentId' in draftResponse.data
  ) {
    return String((draftResponse.data as Record<string, unknown>).contentId);
  }

  // 如果 data 字段本身就是 contentId (字符串)
  if ('data' in draftResponse && typeof draftResponse.data === 'string') {
    return draftResponse.data;
  }

  return undefined;
}

/**
 * 转换附件格式为 API 需要的格式
 */
export function transformAttachments(attachments: AttachmentsModel[]) {
  return attachments.map(attachment => ({
    fileName: attachment.fileName,
    fileSize: attachment.fileSize,
    type: attachment.type === 'MAIL_PICS' ? 1 : 0,
    downloadUrl: attachment.downloadUrl,
    expireTime: attachment.expireTime ?? 0,
    nosKey: attachment.nosKey,
  }));
}

/**
 * 检查 HTML 内容是否为空
 * 判断条件：
 * 1. 内容为空字符串
 * 2. body 中只有空标签（如 <p><br></p>, <p></p>, <br> 等）
 * 3. 去除所有 HTML 标签后没有实际文本内容
 */
export function isHtmlContentEmpty(html: string): boolean {
  if (!html || html.trim() === '') {
    return true;
  }

  // 创建一个临时 DOM 元素来解析 HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // 获取 body 元素（如果存在）或直接使用解析后的内容
  const bodyElement = tempDiv.querySelector('body');
  const contentElement = bodyElement || tempDiv;

  // 检查是否只有空标签
  // 常见的空标签模式：<p><br></p>, <p></p>, <br>, <div><br></div> 等
  const textContent = contentElement.textContent || '';
  const trimmedText = textContent.trim();

  // 如果没有文本内容，认为是空的
  if (trimmedText === '') {
    return true;
  }

  // 检查是否只有换行符和空白字符
  if (/^[\s\n\r]*$/.test(trimmedText)) {
    return true;
  }

  return false;
}

interface UseAutoSaveDraftOptions {
  /** 编辑器引用 */
  editorRef: React.RefObject<MailEditorRef>;
  /** 邮件主题 */
  subject: string;
  /** 草稿ID */
  draftId: string | null;
  /** 联系人ID */
  contactId: string | undefined;
  /** 是否正在编辑（通过编辑器焦点状态判断） */
  isEditing?: boolean;
  /** 自动保存延迟时间（毫秒），默认10秒 */
  delay?: number;
  /** 是否启用自动保存，默认true */
  enabled?: boolean;
}

/**
 * 自动保存草稿 Hook
 *
 * 功能：
 * - 监听 subject、content、attachments 的变化
 * - 操作后指定时间（默认10秒）无后续操作时，触发自动保存
 * - 用户正在上传附件或正在编辑时，阻止自动保存
 * - 自动保存过程中用户如果操作，取消本次保存，等待下次自动保存
 */
export function useAutoSaveDraft({
  editorRef,
  subject,
  draftId,
  contactId,
  isEditing = false,
  delay = 3000,
  enabled = true,
}: UseAutoSaveDraftOptions) {
  const { uploading, setDraftId, setIsAutoSaving, quotedContent } = useMailEditorStore();
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef<boolean>(false);
  const lastContentRef = useRef<string>('');
  const lastSubjectRef = useRef<string>('');
  const lastAttachmentsRef = useRef<AttachmentsModel[]>([]);
  const isInitializedRef = useRef<boolean>(false);
  // 使用 ref 存储最新的 subject，确保定时器回调中总是获取到最新值
  const subjectRef = useRef<string>(subject);

  /**
   * 检查附件列表是否发生变化
   */
  const attachmentsChanged = useCallback((current: AttachmentsModel[], last: AttachmentsModel[]) => {
    if (current.length !== last.length) {
      return true;
    }
    // 比较每个附件的关键属性
    return current.some((curr, index) => {
      const lastItem = last[index];
      return (
        !lastItem ||
        curr.identity !== lastItem.identity ||
        curr.nosKey !== lastItem.nosKey ||
        curr.downloadUrl !== lastItem.downloadUrl
      );
    });
  }, []);

  /**
   * 检查内容是否有变化
   */
  const hasContentChanged = useCallback(() => {
    if (!editorRef.current) {
      return false;
    }

    const currentContent = editorRef.current.getContent() || '';
    // 使用 ref 获取最新的 subject，避免闭包问题
    const currentSubject = subjectRef.current || '';
    const currentAttachments = editorRef.current.getAttachments() || [];

    const contentChanged = currentContent !== lastContentRef.current;
    const subjectChanged = currentSubject !== lastSubjectRef.current;
    const attachmentsChangedFlag = attachmentsChanged(currentAttachments, lastAttachmentsRef.current);

    return contentChanged || subjectChanged || attachmentsChangedFlag;
  }, [editorRef, attachmentsChanged]);

  /**
   * 执行自动保存
   */
  const performAutoSave = useCallback(async () => {
    // 如果正在保存，忽略
    if (isSavingRef.current) {
      return;
    }

    // 检查是否有 contactId（draftId 可以为空，首次写信时没有 draftId）
    if (!contactId || !editorRef.current) {
      return;
    }

    // 检查是否正在上传或正在编辑
    const isUploading = uploading || editorRef.current.getUploading();
    if (isUploading || isEditing) {
      console.log('Auto save skipped: uploading or editing', { uploading: isUploading, isEditing });
      return;
    }

    // 检查内容是否有变化
    if (!hasContentChanged()) {
      console.log('Auto save skipped: no content changes');
      return;
    }

    // 使用 HTML 合并函数安全地合并编辑器内容和引文内容
    const editorContent = editorRef.current.getContent() || '';
    const currentContent = mergeHtmlContent(editorContent, quotedContent || '');
    // 使用 ref 获取最新的 subject，避免闭包问题
    const currentSubject = subjectRef.current || '';
    const currentAttachments = editorRef.current.getAttachments() || [];

    // 允许保存空草稿，不再限制空内容保存

    // 标记正在保存
    isSavingRef.current = true;
    setIsAutoSaving(true);

    try {
      const draftResponse = await editDraftEmailPost({
        contactId,
        contentId: draftId || undefined, // draftId 可以为空，首次创建草稿时为空
        subject: currentSubject,
        content: currentContent,
        manualSave: 0, // 0 表示自动保存
        attachments: transformAttachments(currentAttachments),
      });

      // 从响应中提取 contentId（首次创建草稿时会返回新的 draftId）
      const contentId = extractContentIdFromResponse(draftResponse);

      // 如果返回了 contentId，同步到 store
      if (contentId) {
        setDraftId(contentId, contactId);
      }

      // 保存成功后，更新最后保存的内容
      lastContentRef.current = currentContent;
      lastSubjectRef.current = currentSubject;
      lastAttachmentsRef.current = currentAttachments;

      console.log('Auto save draft succeeded', { contentId, wasNewDraft: !draftId });
    } catch (err) {
      console.error('Auto save draft failed:', err);
      // 自动保存失败不影响用户操作，不显示错误提示
    } finally {
      isSavingRef.current = false;
      setIsAutoSaving(false);
    }
  }, [
    draftId,
    contactId,
    uploading,
    isEditing,
    editorRef,
    quotedContent,
    hasContentChanged,
    setDraftId,
    setIsAutoSaving,
  ]);

  /**
   * 检查内容是否为空（标题、内容和附件都为空）
   */
  const isContentEmpty = useCallback(() => {
    if (!editorRef.current) {
      return true;
    }
    const currentContent = editorRef.current.getContent() || '';
    // 使用 ref 获取最新的 subject，避免闭包问题
    const currentSubject = subjectRef.current || '';
    const currentAttachments = editorRef.current.getAttachments() || [];

    // 检查 HTML 内容是否为空（包括只有 <p><br></p> 的情况）
    const isHtmlEmpty = isHtmlContentEmpty(currentContent);

    return isHtmlEmpty && !currentSubject && currentAttachments.length === 0;
  }, [editorRef]);

  /**
   * 启动或重启自动保存定时器
   */
  const startAutoSaveTimer = useCallback(() => {
    // 如果未启用，不启动定时器
    if (!enabled) {
      return;
    }

    // 如果还未初始化完成，不启动定时器（首次进入时避免误触发）
    if (!isInitializedRef.current) {
      return;
    }

    // 清除之前的定时器
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // 如果正在保存，不启动新的定时器
    if (isSavingRef.current) {
      return;
    }

    // 检查是否正在上传或正在编辑
    const isUploading = uploading || editorRef.current?.getUploading();
    if (isUploading || isEditing) {
      console.log('Auto save timer not started: uploading or editing', {
        uploading: isUploading,
        isEditing,
      });
      return;
    }

    // 启动定时器（不需要检查 draftId，首次写信时也可以自动保存）
    // 允许保存空草稿
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
      autoSaveTimerRef.current = null;
    }, delay);

    console.log(`Auto save timer started, will save after ${delay}ms`);
  }, [enabled, uploading, isEditing, performAutoSave, delay, editorRef]);

  /**
   * 监听 store 中的 content 和 attachments 变化
   */
  const { attachments } = useMailEditorStore();

  /**
   * 同步 subject 到 ref，确保定时器回调中总是获取到最新值
   */
  useEffect(() => {
    subjectRef.current = subject || '';
  }, [subject]);

  /**
   * 当 contactId 或 enabled 变化时，重置初始化状态
   * 这样可以确保切换联系人或启用/禁用时重新初始化
   */
  useEffect(() => {
    // 重置初始化状态，等待重新初始化
    isInitializedRef.current = false;
    // 清除定时器
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, [contactId, enabled]);

  /**
   * 初始化：首次进入时同步初始值，但不触发自动保存
   * 这样可以避免首次进入时因为参数变化而误触发自动保存
   */
  useEffect(() => {
    if (!enabled || !contactId || !editorRef.current) {
      return;
    }

    // 如果已经初始化过，不再执行
    if (isInitializedRef.current) {
      return;
    }

    // 获取当前内容并同步到 lastContentRef，但不触发保存
    const editorInstance = editorRef.current.getEditorInstance();
    if (!editorInstance) {
      return;
    }

    // 使用 ref 获取最新的 subject，确保获取到最新值
    const currentSubject = subjectRef.current || '';
    lastSubjectRef.current = currentSubject;
    // 使用 setTimeout 确保在编辑器完全初始化后再同步初始值
    // 这样可以避免编辑器初始化过程中的变化触发自动保存
    const initTimer = setTimeout(() => {
      if (!editorRef.current) {
        return;
      }

      // 同步初始值
      const currentContent = editorRef.current.getContent() || '';

      const currentAttachments = editorRef.current.getAttachments() || [];
      lastContentRef.current = currentContent;
      lastAttachmentsRef.current = currentAttachments;
      // 标记为已初始化
      isInitializedRef.current = true;
      console.log('Auto save initialized with initial values', {
        contentLength: currentContent.length,
        subject: currentSubject,
        attachmentsCount: currentAttachments.length,
      });
    }, 100); // 延迟 100ms 确保编辑器完全初始化

    return () => {
      clearTimeout(initTimer);
    };
  }, [enabled, contactId, editorRef, subject]);

  /**
   * 监听编辑器内容变化事件（包括 link URL 变更等）
   * 当编辑器触发 change 事件时，检查内容是否有变化并启动自动保存定时器
   */
  useEffect(() => {
    if (!enabled || !contactId || !editorRef.current) {
      return;
    }

    // 如果还未初始化完成，不绑定事件监听器
    if (!isInitializedRef.current) {
      return;
    }

    // 获取编辑器实例
    const editorInstance = editorRef.current.getEditorInstance();
    if (!editorInstance) {
      return;
    }

    // 类型检查：确保编辑器实例有 on 和 off 方法
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editor = editorInstance as any;
    if (typeof editor.on !== 'function' || typeof editor.off !== 'function') {
      return;
    }

    // 监听编辑器的 change 事件
    const handleEditorChange = () => {
      // 检查内容是否有变化
      if (hasContentChanged()) {
        // 内容有变化，重新启动定时器
        startAutoSaveTimer();
      }
    };

    // 绑定事件监听器
    editor.on('change', handleEditorChange);
    // 也监听 NodeChange 事件，因为某些 DOM 操作（如 setAttrib）可能只触发 NodeChange
    editor.on('NodeChange', handleEditorChange);

    // 清理函数：移除事件监听器
    return () => {
      if (editor) {
        editor.off('change', handleEditorChange);
        editor.off('NodeChange', handleEditorChange);
      }
    };
  }, [enabled, contactId, editorRef, hasContentChanged, startAutoSaveTimer]);

  /**
   * 监听 subject、content、attachments 变化，立即重新启动定时保存
   * 这些变化通常来自用户操作，需要立即响应
   */
  useEffect(() => {
    if (!enabled || !contactId) {
      // 如果未启用或没有 contactId，清除定时器
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    // 如果还未初始化完成，不触发自动保存（首次进入时避免误触发）
    if (!isInitializedRef.current) {
      return;
    }

    // 检查内容是否有变化
    if (hasContentChanged()) {
      // 内容有变化，重新启动定时器
      startAutoSaveTimer();
    }
  }, [subject, attachments, enabled, contactId, startAutoSaveTimer, hasContentChanged]);

  /**
   * 监听上传状态变化，如果上传完成且内容有变化，重新启动定时器
   */
  // useEffect(() => {
  //   if (!enabled || !contactId) {
  //     return;
  //   }

  //   // 如果上传完成（从 uploading 变为 false），且内容有变化，重新启动定时器
  //   if (!uploading && hasContentChanged()) {
  //     startAutoSaveTimer();
  //   }
  // }, [uploading, enabled, contactId, startAutoSaveTimer, hasContentChanged]);

  /**
   * 清除定时器（用于手动保存时）
   */
  const clearAutoSaveTimer = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);

  /**
   * 重置最后保存的状态（用于编辑器被清空时）
   * 同步更新 lastContentRef, lastSubjectRef, lastAttachmentsRef，确保 hasContentChanged 返回 false
   */
  const resetLastSavedState = useCallback(() => {
    lastContentRef.current = '';
    lastSubjectRef.current = '';
    lastAttachmentsRef.current = [];
    // 重置初始化状态，下次需要重新初始化
    isInitializedRef.current = false;
    // 清除定时器
    clearAutoSaveTimer();
  }, [clearAutoSaveTimer]);

  /**
   * 监听 draftId 变化，当 draftId 被清空（变为 null）且内容为空时，重置最后保存的状态
   * 这用于处理手动保存后清空编辑器的情况
   */
  useEffect(() => {
    // 如果 draftId 被清空（从有值变为 null）且内容为空，重置状态
    if (draftId === null && isContentEmpty()) {
      resetLastSavedState();
    }
  }, [draftId, isContentEmpty, resetLastSavedState]);

  /**
   * 组件卸载时清理定时器
   */
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, []);

  /**
   * 手动触发保存（用于测试或特殊情况）
   */
  const triggerAutoSave = useCallback(() => {
    performAutoSave();
  }, [performAutoSave]);

  return {
    triggerAutoSave,
    clearAutoSaveTimer,
    resetLastSavedState,
  };
}
